import * as ramda from 'ramda';

import type {FilterObject, EntityListViewType} from 'nutshell-core/types';

import {getDefaultStagesetId} from '../../../util/get-default-stageset-id';

// @ts-expect-error
import {handleFilterUpdate, handleFilterRemove} from './filter';

/**
 * Called whenever the "view" (board, map, list, chart) changes. If the new view
 * is the board, we'll sanitize our pipeline filter, and remove any stage filter
 * that might exist.
 *
 * @param  {Object[]} existingFilters   - Array of filter objects
 * @param  {string}   newView           - string id of new view
 * @return {Object[]}                   - New array of filter objects, adjusted for singular pipeline
 */
export function sanitizeFiltersForView(
    existingFilters: FilterObject[],
    newView: EntityListViewType
): FilterObject[] {
    let sanitizedFilters = clearStagesetFilterForNonMultipleFunnelUsers(existingFilters, newView);

    if (newView !== 'board') return sanitizedFilters;

    sanitizedFilters = sanitizePipelineFilter(sanitizedFilters);

    // Filter our stage filter
    return sanitizedFilters.filter((filter) => {
        return Object.keys(filter)[0] !== 'stage' && Object.keys(filter)[0] !== 'milestone';
    });
}

/**
 * When changing to a non-board view, if multiple funnels are not available, clear the
 * stageset filter
 *
 * @param  {Object[]} existingFilters   - Array of filter objects
 * @param  {string}   newView           - string id of new view
 * @return {Object[]}                   - New array of filter objects
 */
function clearStagesetFilterForNonMultipleFunnelUsers(
    existingFilters: FilterObject[],
    newView: EntityListViewType
): FilterObject[] {
    if (newView === 'board') return existingFilters;

    // Default case - we do nothing to our existing filters
    let newFilters = existingFilters;

    if (!NutCapabilities.multipleFunnel.isAvailable) {
        const existingStagesetFilter = ramda.find(ramda.has('stageset'), existingFilters);
        if (existingStagesetFilter) {
            newFilters = handleFilterRemove(existingFilters, [existingStagesetFilter]);
        }
    }

    return newFilters;
}

/**
 * Adjust our stageset filter to ensure exactly one (no more, no less) is applied.
 * If the stageset filter does not exist, we'll add the default stageset.
 *
 * @param  {Object[]} existingFilters       Existing filter array
 * @return {Object[]}                       New array of filter objects, adjusted for singular pipeline
 */
// @ts-expect-error
function sanitizePipelineFilter(existingFilters): FilterObject[] {
    // Default case - we do nothing to our existing filters
    let newFilters = existingFilters;

    // Get the current stageset filter from our array of active filters
    const existingStagesetFilter = ramda.find(ramda.has('stageset'), existingFilters);
    const defaultStagesetFilter = getLeadsDefaultPipelineFilter();

    if (existingStagesetFilter) {
        // @ts-expect-error
        const sanitizedStagesetFilters = existingStagesetFilter.stageset.data.filter((item) => {
            return item.data !== '-';
        });

        // If there are multiple non-null stagesets, only use first
        if (
            sanitizedStagesetFilters.length > 1 ||
            // @ts-expect-error
            existingStagesetFilter.stageset.data.length > 1
        ) {
            const newStagesetFilter = {
                stageset: {
                    // @ts-expect-error
                    anyAll: existingStagesetFilter.stageset.anyAll,
                    data: [sanitizedStagesetFilters[0]],
                },
            } as const;

            newFilters = handleFilterUpdate(existingFilters, {
                oldFilter: existingStagesetFilter,
                newFilter: newStagesetFilter,
            });
            // This case occurs when there was previously one stageset filter applied,
            // and it was the `None` case. In which case we'll just apply the default
            // stageset filter instead.
        } else if (sanitizedStagesetFilters.length === 0) {
            newFilters = handleFilterUpdate(existingFilters, {
                oldFilter: existingStagesetFilter,
                newFilter: defaultStagesetFilter,
            });
        }
    } else {
        newFilters = existingFilters.concat(defaultStagesetFilter);
    }

    return newFilters;
}

export function getLeadsDefaultPipelineFilter(): FilterObject {
    return {
        stageset: {anyAll: 'any', data: [{data: getDefaultStagesetId()}]},
    };
}
